/**
 * Web Push Notifications — Service Worker registration and subscription management.
 */

import { savePushSubscription, removePushSubscription } from '@/db/pushSubscriptions';
import { logger } from '@/utils/logger';

const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY as string;

/** Convert URL-safe base64 to Uint8Array (required by pushManager.subscribe) */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/** Check if push notifications are supported */
export function isPushSupported(): boolean {
  return (
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window &&
    !!VAPID_PUBLIC_KEY
  );
}

/** Get current notification permission state */
export function getPermissionState(): NotificationPermission {
  if (!('Notification' in window)) return 'denied';
  return Notification.permission;
}

/** Register the service worker (idempotent) */
export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null;
  try {
    const registration = await navigator.serviceWorker.register('/sw.js');
    logger.info('[Push] Service worker registered');
    return registration;
  } catch (err) {
    logger.warn('[Push] SW registration failed:', err);
    return null;
  }
}

/** Request notification permission from the user */
export async function requestPermission(): Promise<NotificationPermission> {
  if (!('Notification' in window)) return 'denied';
  const result = await Notification.requestPermission();
  return result;
}

/**
 * Subscribe to push notifications and save to DB.
 * Returns the subscription or null if failed.
 */
export async function subscribeToPush(userId: string): Promise<PushSubscription | null> {
  if (!isPushSupported()) return null;

  const permission = await requestPermission();
  if (permission !== 'granted') return null;

  const registration = await navigator.serviceWorker.ready;
  try {
    // Check for existing subscription first
    let subscription = await registration.pushManager.getSubscription();
    if (!subscription) {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY).buffer as ArrayBuffer,
      });
    }

    // Save to database
    await savePushSubscription(userId, subscription.toJSON());
    logger.info('[Push] Subscribed and saved');
    return subscription;
  } catch (err) {
    logger.warn('[Push] Subscription failed:', err);
    return null;
  }
}

/**
 * Unsubscribe from push notifications and remove from DB.
 */
export async function unsubscribeFromPush(userId: string): Promise<void> {
  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.getSubscription();
  if (subscription) {
    const endpoint = subscription.endpoint;
    await subscription.unsubscribe();
    await removePushSubscription(userId, endpoint).catch(() => {});
    logger.info('[Push] Unsubscribed');
  }
}

/**
 * Check if the user is currently subscribed.
 */
export async function isSubscribed(): Promise<boolean> {
  if (!isPushSupported()) return false;
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    return !!subscription;
  } catch {
    return false;
  }
}

/**
 * Send a test notification locally (for debugging).
 */
export async function sendTestNotification(): Promise<void> {
  const registration = await navigator.serviceWorker.ready;
  await registration.showNotification('Zenda — Test', {
    body: 'Le notifiche push funzionano correttamente!',
    icon: '/zenda.svg',
    tag: 'test-notification',
  });
}
